import { inject, Injectable } from '@angular/core';
import { QuoteService } from '@lx/quote-tool/shared/data-access/quote';
import { HostDataReaderService, QuoteToolStrategyService } from '@lx/quote-tool/shared/services';
import { EnrolmentPaymentInfo } from '@lx/quote-tool/shared/types';
import { getQuoteToolStorageKey } from '@lx/quote-tool/lite/shared/types';
import { ResidenceCountry, ResidenceCountryService } from '@lx/shared/data-access/destination';
import { LoggerService } from '@lx/shared/logger';
import { SessionStorageService } from '@lx/shared/storage';
import { normalizeString, QuoteToolPreselectedModes, removeLineBreaks } from '@lx/shared/utils';
import { combineLatest, Observable, of, tap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { z, ZodSafeParseResult } from 'zod';
import { QuoteToolLiteState } from '../../../store';
import { RouteIds } from '../../../types';
import { PaymentDeepLinkParameters } from '../../../types/payment-deep-link-parameters.type';
import { StepDefinitionService } from '../../step-definition.service';
import { PaymentDeepLinkParametersSchema } from '../../../schemas';
import { Flows } from '@lx/quote-tool/lite/shared/types';

@Injectable({
  providedIn: 'root',
})
export class PaymentModeStrategyService implements QuoteToolStrategyService<RouteIds> {
  private readonly hostDataReader = inject(HostDataReaderService);
  private readonly logger = inject(LoggerService);
  private readonly quoteService = inject(QuoteService);
  private readonly residenceCountryService = inject(ResidenceCountryService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly stepDefinitionService = inject(StepDefinitionService);
  private readonly storageKey = getQuoteToolStorageKey();

  getQuoteToolMode(): QuoteToolPreselectedModes {
    return QuoteToolPreselectedModes.payment;
  }

  getStepToNavigate(): Observable<RouteIds> | RouteIds {
    return of(RouteIds.payment);
  }

  loadData$(): Observable<boolean> {
    const deepLinkParameters = this.getDeepLinkParameters();

    const parsedParameters = this.parseDeepLinkParameters(deepLinkParameters);

    if (!parsedParameters.success) {
      this.logger.error('Invalid deep link parameters', z.prettifyError(parsedParameters.error));
      return of(false);
    }

    const { leadId, token } = deepLinkParameters;
    const cleanedToken = removeLineBreaks(token);

    return combineLatest([this.quoteService.getEnrolmentFromEnrolmentIdV2(leadId, cleanedToken), this.residenceCountryService.getAll()]).pipe(
      map(([enrolmentPaymentInfo, countries]) => this.preparePaymentDataForStore(countries, enrolmentPaymentInfo)),
      tap((data) => {
        if (data) {
          this.sessionStorageService.set(this.storageKey, data);
        }
      }),
      map((data) => Boolean(data)),
      catchError((error) => {
        this.logger.error('Error during loadData$', error);
        return of(false);
      }),
    );
  }

  private preparePaymentDataForStore(
    countries: ResidenceCountry[],
    enrolmentPaymentInfo: EnrolmentPaymentInfo | null,
  ): Partial<QuoteToolLiteState & { currentStepId: string }> | null {
    if (!enrolmentPaymentInfo) {
      return null;
    }

    const { leadInput, flywireCheckoutSessionData } = enrolmentPaymentInfo;
    const { leadUserData, quotes } = leadInput;
    const [quote] = quotes ?? [];
    if (!quote || !leadUserData) {
      return null;
    }

    const residence = countries.find((c) => normalizeString(c.code) === normalizeString(leadUserData.countryOfResidence));
    if (!residence) {
      return null;
    }

    const { code: countryCode, currency, phoneCode } = residence;

    const {
      headerData: { selectedCurrency },
      quoteData: { payableData },
    } = quote;

    if (!payableData) {
      return null;
    }

    const activeFlow = leadUserData.isMinor ? Flows.Junior : Flows.Adult;

    return {
      studentDetails: leadUserData,
      activeFlow,
      currentStepId: this.stepDefinitionService.getStepDefinition(RouteIds.payment, activeFlow)?.id,
      selectedCurrency,
      depositAmounts: payableData.amounts,
      selectedResidence: { phoneCode, currency, countryCode },
      flywireCheckoutSessionData: flywireCheckoutSessionData,
    };
  }

  private getDeepLinkParameters(): PaymentDeepLinkParameters {
    // Get query params from URL
    const queryParams = this.hostDataReader.getQueryParams();
    const quoteToolModeQueryParam = this.hostDataReader.getQuoteToolMode();
    return {
      preselectedMode: quoteToolModeQueryParam ? (quoteToolModeQueryParam as QuoteToolPreselectedModes) : undefined,
      leadId: queryParams.get('leadId') || undefined,
      token: queryParams.get('token') || undefined,
    };
  }

  private parseDeepLinkParameters(deepLinkParameters: PaymentDeepLinkParameters): ZodSafeParseResult<Partial<PaymentDeepLinkParameters>> {
    return PaymentDeepLinkParametersSchema.safeParse(deepLinkParameters);
  }
}
